import React from "react"
import i18n from "@/i18n"
import { type SidebarTimeSlot } from "../reservation_sidebar_availability"

type TimeStepProps = {
  startTime: string
  hasConfirmedSelection: boolean
  timeSlots: SidebarTimeSlot[]
  onTimeSelect: (startTime: string) => void
  onContinue: () => void
  showContinueButton?: boolean
}

const ACTION_BUTTON_STYLES =
  "mt-5 flex h-[34px] w-full items-center justify-center rounded-[5px] bg-[#2596fa] text-sm font-medium text-white transition-colors hover:bg-[#1f84dd] disabled:cursor-not-allowed disabled:bg-[rgba(37,150,250,0.6)]"

const TimeStep: React.FC<TimeStepProps> = ({
  startTime,
  hasConfirmedSelection,
  timeSlots,
  onTimeSelect,
  onContinue,
  showContinueButton = true,
}) => {
  const hasAvailableTimeOptions = timeSlots.length > 0
  const hasSelection =
    hasConfirmedSelection &&
    Boolean(startTime) &&
    timeSlots.some((slot) => slot.startTime === startTime && slot.available)

  return (
    <div className="flex flex-col" data-testid="public-venue-sidebar-time-step">
      {hasAvailableTimeOptions ? (
        <div className="grid grid-cols-4 gap-x-[4px] gap-y-[6px]" data-testid="public-venue-sidebar-time-grid">
          {timeSlots.map((slot) => {
            const isSelected = hasSelection && startTime === slot.startTime

            return (
              <button
                key={slot.startTime}
                type="button"
                disabled={!slot.available}
                onClick={() => onTimeSelect(slot.startTime)}
                aria-pressed={isSelected}
                aria-label={slot.label}
                data-selected={isSelected}
                data-testid={`public-venue-sidebar-time-option-${slot.label}`}
                className={`flex h-[30px] items-center justify-center rounded-[3px] border text-[14px] text-center leading-5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2596fa] ${
                  !slot.available
                    ? "cursor-not-allowed border-[#e5e7eb] bg-white text-[rgba(23,23,23,0.35)]"
                    : isSelected
                      ? "border-[#2596fa] bg-[#2596fa] font-medium text-white"
                      : "border-[#e5e7eb] bg-white font-normal text-[rgba(23,23,23,0.7)] hover:border-[#cbcbcb] hover:bg-[#f3f3f3] hover:font-medium"
                }`}
              >
                {slot.label}
              </button>
            )
          })}
        </div>
      ) : (
        <div
          className="rounded-[8px] border border-dashed border-[#d6dbe2] bg-white/80 px-4 py-5 text-center text-xs leading-5 text-[#5f6774]"
          data-testid="public-venue-sidebar-time-unavailable"
        >
          {i18n.t("app.public.venue.sidebar.time_unavailable")}
        </div>
      )}

      {showContinueButton && (
        <button
          type="button"
          onClick={onContinue}
          disabled={!hasSelection}
          className={ACTION_BUTTON_STYLES}
          data-testid="public-venue-sidebar-reserve-btn"
        >
          {i18n.t("app.public.venue.sidebar.select_time")}
        </button>
      )}
    </div>
  )
}

export default TimeStep
